import styled from '@emotion/styled';
import React, { type ReactNode } from 'react';
import {
	type CardOrientation,
	isRelativeUrl,
	StyledCard,
	VisuallyHidden,
} from '../../../../../ui';
import type Responsive from '../../../../../ui/utils/Responsive';
import { useTranslation } from '../../../i18n/translate/useTranslation';
import { RouterLink } from '../../link/RouterLink';

interface IBaseCardProps {
	fullyClickable?: boolean;
	linkHref: string;
	cardOrientation?: Responsive.IValueOrResponsiveValue<CardOrientation>;
	titleFontSize?: Responsive.IValueOrResponsiveValue<number>;
	children: ReactNode;
	variant: 'horizontal' | 'vertical';
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function BaseCard({
	fullyClickable = true,
	linkHref,
	cardOrientation = 'horizontal',
	titleFontSize = 32,
	variant,
	children,
}: IBaseCardProps) {
	const isExternalUrl = !isRelativeUrl(linkHref);
	const { t } = useTranslation();

	return (
		<CardStyled
			fullyClickable={fullyClickable}
			cardOrientation={cardOrientation}
			titleFontSize={titleFontSize}
		>
			<StyledLink
				href={linkHref}
				variant={variant}
				target={isExternalUrl ? '_blank' : undefined}
				removeUnderlineStyling
			>
				<>
					{children}
					{isExternalUrl && (
						<VisuallyHidden as="span">
							{t('generic_external_link_label')}
						</VisuallyHidden>
					)}
				</>
			</StyledLink>
		</CardStyled>
	);
}

const StyledLink = styled(RouterLink)<{ variant: 'horizontal' | 'vertical' }>`
	text-decoration: none;
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	align-self: stretch;

	${({ variant }) =>
		variant === 'horizontal' &&
		`
			display: flex;
			flex-direction: row;
	`};
`;

export const CardStyled = styled(StyledCard)<{
	fullyClickable?: boolean;
}>`
	flex-grow: 1;
	border-radius: 12px;
	margin-left: 0;
	margin-top: 0;
	position: relative;

	box-shadow: none;
	transition: transform 0.2s ease-in-out;

	&:hover {
		transform: translateY(-12px);
		box-shadow: none;
	}

	${StyledLink} {
		&:focus-visible {
			outline: 2px solid #430042;
			outline-offset: -2px;
			border-radius: 12px;
		}
	}
`;
